<template>
    <div class="container shadow-sm">
        <EditionModal id="editMapObjectStatusTemplateModal" ref="editionModal" v-bind:editTitle="$t('admin.configuration.status.edit_status_title')" v-bind:createTitle="$t('admin.configuration.status.new_status_title')" 
                v-bind:model="editedMapObjectStatusTemplate" v-on:save="handleSaveEditedMapObjectStatusTemplateClicked()" v-on:cancel="handleCancelEditedMapObjectStatusTemplateClicked()">
            <VerticalScrollLayout v-if="editedMapObjectStatusTemplate">
                <VSLMultilingualTextInputRowRenderer v-bind:label="$t('admin.configuration.status.title_label')" v-bind:value="editedMapObjectStatusTemplate.title" />
                <VSLCheckboxRowRenderer v-bind:label="$t('admin.configuration.status.active_label')" v-model="editedMapObjectStatusTemplate.active" />
                <VSLCheckboxRowRenderer v-bind:label="$t('admin.configuration.status.excluding_from_list_label')" v-model="editedMapObjectStatusTemplate.excludeFromWebViewerList" />
                <VSLColorPickerRowRenderer v-bind:label="$t('admin.configuration.status.main_color_label')" v-model="editedMapObjectStatusTemplate.color" />
            </VerticalScrollLayout>
        </EditionModal>

        <YesNoAlert ref="confirmDeleteAlert" id="confirmDeleteAlert" v-bind:title="$t('admin.configuration.status.confirm_delete_title')" v-bind:message="$t('admin.configuration.status.confirm_delete_message')" v-on:yes="handleDeleteMapObjectStatusTemplateConfirmed()" />

        <div class="toolbar">
            <button class="btn btn-primary btn-sm" v-on:click="handleNewMapObjectStatusTemplateClicked()">{{ $t("admin.configuration.status.new_status_button") }}</button>
        </div>

        <div class="status-list-container">
            <div class="empty-list" v-if="!mapObjectStatusTemplates || mapObjectStatusTemplates.length == 0">{{ $t("admin.configuration.status.no_map_object_status_templates") }}</div>
            <ul class="status-list" v-if="mapObjectStatusTemplates && mapObjectStatusTemplates.length > 0">
                <li class="status-container" v-for="mapObjectStatusTemplate of this.mapObjectStatusTemplates" v-bind:key="mapObjectStatusTemplate.id">
                    <div class="badges">
                        <span class="badge bg-success" v-if="mapObjectStatusTemplate.active">{{ $t("admin.configuration.status.active_badge") }}</span>
                        <span class="badge bg-danger" v-if="!mapObjectStatusTemplate.active">{{ $t("admin.configuration.status.inactive_badge") }}</span>
                    </div>
                    <div class="title">{{ mapObjectStatusTemplateTitle(mapObjectStatusTemplate) }}</div>
                    <div class="color" v-if="mapObjectStatusTemplate.color" v-bind:style="'background-color: ' + mapObjectStatusTemplate.color"></div>
                    <div class="actions">
                        <button class="btn btn-outline-primary btn-sm" v-on:click="handleEditButtonClicked(mapObjectStatusTemplate)">{{ $t("admin.configuration.status.edit_button") }}</button>
                        <button class="btn btn-outline-primary btn-sm" v-if="!mapObjectStatusTemplate.active" v-on:click="handleActivateButtonClicked(mapObjectStatusTemplate)">{{ $t("admin.configuration.status.activate_button") }}</button>
                        <button class="btn btn-outline-primary btn-sm" v-if="mapObjectStatusTemplate.active" v-on:click="handleDeactivateButtonClicked(mapObjectStatusTemplate)">{{ $t("admin.configuration.status.deactivate_button") }}</button>
                        <button class="btn btn-outline-danger btn-sm" v-on:click="handleDeleteButtonClicked(mapObjectStatusTemplate)">{{ $t("admin.configuration.status.delete_button") }}</button>
                    </div>
                    <hr />
                </li>
            </ul>
        </div>
    </div>
</template>

<script>
import MapObjectStatusService from "@/service/MapObjectStatusService.js"
import MapObjectStatusTemplate from "@/model/MapObjectStatusTemplate.js"
import LangUtils from "@/utils/LangUtils.js"
import YesNoAlert from "@/views/ui/alert/YesNoAlert"
import EditionModal from "@/views/ui/modal/EditionModal"
import VerticalScrollLayout from "@/views/ui/verticalScrollLayout/VerticalScrollLayout"
import VSLMultilingualTextInputRowRenderer from "@/views/ui/verticalScrollLayout/renderer/VSLMultilingualTextInputRowRenderer"
import VSLCheckboxRowRenderer from "@/views/ui/verticalScrollLayout/renderer/VSLCheckboxRowRenderer"
import VSLColorPickerRowRenderer from "@/views/ui/verticalScrollLayout/renderer/VSLColorPickerRowRenderer"

export default {
    components: {
        YesNoAlert,
        EditionModal,
        VerticalScrollLayout,
        VSLMultilingualTextInputRowRenderer,
        VSLCheckboxRowRenderer,
        VSLColorPickerRowRenderer
    },

    data() {
        return {
            selectedPublisher: this.$store.state.currentPublisher,
            mapObjectStatusService: new MapObjectStatusService(this.$i18n.locale),
            mapObjectStatusTemplates: null,
            editedMapObjectStatusTemplate: null,
            mapObjectStatusTemplateToDelete: null
        }
    },

    methods: {
        loadMapObjectStatusTemplates() {
            if (this.selectedPublisher) {
                this.mapObjectStatusService.loadMapObjectStatusTemplates(this.selectedPublisher.id).then((mapObjectStatusTemplates) => {
                    this.mapObjectStatusTemplates = mapObjectStatusTemplates;
                }).catch((error) => {
                    console.log(error);
                });
            }
        },

        saveMapObjectStatusTemplate(mapObjectStatusTemplate, reloadList) {
            this.mapObjectStatusService.saveMapObjectStatusTemplate(this.selectedPublisher.id, mapObjectStatusTemplate).then(() => {
                if (reloadList) {
                    this.loadMapObjectStatusTemplates();
                }
            }).catch((error) => {
                console.log(error);
            });
        },

        deleteMapObjectStatusTemplate(mapObjectStatusTemplate) {
            this.mapObjectStatusService.deleteMapObjectStatusTemplate(this.selectedPublisher.id, mapObjectStatusTemplate.id).then(() => {
                this.loadMapObjectStatusTemplates();
            }).catch((error) => {
                console.log(error);
            });
        },

        mapObjectStatusTemplateTitle(mapObjectStatusTemplate) {
            return LangUtils.valueForLanguage(mapObjectStatusTemplate.title, this.$i18n.locale);
        },

        handleNewMapObjectStatusTemplateClicked() {
            this.editedMapObjectStatusTemplate = new MapObjectStatusTemplate();
            this.editedMapObjectStatusTemplate.title = {
                en: null,
                fr: null
            }
            this.editedMapObjectStatusTemplate.active = true;
        },

        handleSaveEditedMapObjectStatusTemplateClicked() {
            this.saveMapObjectStatusTemplate(this.editedMapObjectStatusTemplate, true);
            this.editedMapObjectStatusTemplate = null;
        },

        handleCancelEditedMapObjectStatusTemplateClicked() {
            this.editedMapObjectStatusTemplate = null;
        },

        handleEditButtonClicked(mapObjectStatusTemplate) {
            this.editedMapObjectStatusTemplate = new MapObjectStatusTemplate(mapObjectStatusTemplate.toJSON());
        },

        handleActivateButtonClicked(mapObjectStatusTemplate) {
            mapObjectStatusTemplate.active = true;
            this.saveMapObjectStatusTemplate(mapObjectStatusTemplate, false);
        },

        handleDeactivateButtonClicked(mapObjectStatusTemplate) {
            mapObjectStatusTemplate.active = false;
            this.saveMapObjectStatusTemplate(mapObjectStatusTemplate, false);
        },

        handleDeleteButtonClicked(mapObjectStatusTemplate) {
            this.mapObjectStatusTemplateToDelete = mapObjectStatusTemplate;
            this.$refs.confirmDeleteAlert.show();
        },

        handleDeleteMapObjectStatusTemplateConfirmed() {
            if (this.mapObjectStatusTemplateToDelete) {
                this.deleteMapObjectStatusTemplate(this.mapObjectStatusTemplateToDelete);
                this.mapObjectStatusTemplateToDelete = null;
            }
        }
    },

    watch: {
        editedMapObjectStatusTemplate: function() {
            if (this.editedMapObjectStatusTemplate != null) {
                this.$refs.editionModal.show();
            } else {
                this.$refs.editionModal.hide();
            }
        }
    },

    mounted() {
        this.$store.subscribe( (mutation) => {
            if (mutation.type == "setCurrentPublisher") {
                this.selectedPublisher = mutation.payload;
                this.loadMapObjectStatusTemplates();
            }
        });

        this.loadMapObjectStatusTemplates();
    }
}
</script>

<style scoped>
.container {
    background-color: #ffffff;
    display: flex;
    flex-direction: column;
    margin: 0px;
    margin-bottom: 16px;
    padding: 24px;
    width: 100%;
    height: 100%;
    max-width: none;
    text-align: left;
}

.toolbar {
    text-align: left;
    width: 100%;
}

.toolbar img {
    margin-right: 16px;
    width: 50px;
}

.toolbar .title {
    display: inline-block;
    font-size: 16px;
    font-weight: bold;
    margin-right: 36px;
    max-width: 250px;
}

.toolbar button {
    margin: 0px 4px;
}

.status-list-container {
    background-color: #ffffff;
    margin: 0 auto;
    overflow: scroll;
    padding: 24px;
    text-align: left;
    width: 100%;
    height: 100%;
}

.empty-list {
    color: #777777;
    font-size: 36px;
    font-weight: bold;
    margin-top: 64px;
    text-align: center;
}

.status-list {
    list-style-type: none;
    padding: 0px;
    margin-top: 24px;
}

.status-container {
    display: grid;
    grid-template-columns: auto 400px;
    grid-template-rows: auto auto auto auto;
    grid-template-areas: "badges badges" "title actions" "color color" "separator separator";
}

.badges {
    grid-area: badges;
}

.badges span {
    margin-right: 8px;
}

.title {
    grid-area: title;
    font-weight: bold;
}

.color {
    grid-area: color;
    border-radius: 16px;
    border: 1px solid #bfbfbf;
    width: 150px;
    height: 10px;
}

.actions {
    grid-area: actions;
    text-align: right;
}

.actions button {
    margin: 4px 4px;
}

.status-container hr {
    grid-area: separator;
}

</style>